import debug from "debug";
const log = debug("app:downloadCSVPwrTable");

/**
 * A class for generating and downloading CSV data for power tables.
 * @category Components
 * @subcategory PowerTable
 */
export class DownloadCsv {
  /**
   * Initializes a new instance of the DownloadCsv class.
   */
  constructor() {
    /** @param {string} The CSV data string. */
    this.csvData = "";

    this.eventHub = experience.eventHub;
    this.setupEventListeners();
  }

  /**
   * Sets up event listeners for power data changes and the download button.
   */
  setupEventListeners() {
    // Listen for the 'powerDataChanged' event to generate CSV data
    document.addEventListener("powerDataChanged", (event) => {
      log("powerDataChanged event received");
      this.generateCsvData(event.detail);
    });

    // Add click event listener to the download button
    const downloadButton = document.getElementById("powerTable-download-button");
    if (downloadButton) {
      downloadButton.addEventListener("click", () => {
        this.downloadCSV();
      });
    }
  }

  cleanup() {
    console.log("DownloadCsv cleanup started...");
  
    // 1. Remove event listeners
    if (this.boundPowerDataHandler) {
      document.removeEventListener("powerDataChanged", this.boundPowerDataHandler);
      this.boundPowerDataHandler = null;
    }
  
    if (this.downloadButton && this.boundDownloadHandler) {
      this.downloadButton.removeEventListener("click", this.boundDownloadHandler);
      this.boundDownloadHandler = null;
    }
  
    // 2. Revoke Blob URLs
    if (this.currentDownloadUrl) {
      URL.revokeObjectURL(this.currentDownloadUrl);
      this.currentDownloadUrl = null;
    }
  
    // 3. Clear object references
    this.csvData = "";
    this.downloadButton = null;
  
    console.log("DownloadCsv cleanup completed.");
  }

  /**
   * Generates CSV data based on the provided detail object.
   * 
   * @param {Object} detail - The detail object containing table headers, data rows, and total power.
   * @param {Object} detail.header - Mapping of column keys to their display names.
   * @param {Array<Object>} detail.data - Array of row objects containing data for each column.
   * @param {Array} detail.total - Array representing the total power row (e.g., ["Total Power", ...]).
   */
  generateCsvData(detail) {
    this.csvData = "";

    let headerKeys = Object.keys(detail.header);
    for (let i = 0; i < headerKeys.length; i++) {
      let delimiter = (i + 1) === headerKeys.length ? "\n" : ",";
      this.csvData += headerKeys[i] + delimiter;
    }

    detail.data.forEach(row => {
      headerKeys.forEach((key, index) => {
        let actualKey = detail.header[key];
        let value = row[actualKey];
        let delimiter = (index + 1) === headerKeys.length ? "\n" : ",";
        this.csvData += `${value}${delimiter}`;
      });
    });

    // Add the estimated total row at the end
    const totalRow = `Total Power (mW),,,,${detail.total[1]}\n`;
    this.csvData += totalRow;

    log("Generated CSV Data:", this.csvData);
  }

  /**
   * Triggers the download of the generated CSV data as a file.
   */
  downloadCSV() {
    if (!this.csvData) {
      alert("No data available for download.");
      return;
    }

    const blob = new Blob([this.csvData], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'XMW-PowerTable.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
